import React, { useState, useEffect } from 'react';
import { useLocalization } from '../contexts/LocalizationContext';
import { useAuth } from '../contexts/AuthContext';
import OperatorStats from './dashboard/OperatorStats';
import {
  dashboardService,
  DashboardStats,
  BalanceData,
  HourlyChartData,
  DailyStats,
  OperatorStats as OperatorStatsType
} from '../services/dashboardService';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  Legend,
  LineChart,
  Line,
  ComposedChart,
} from 'recharts';
import {
  TrendingUp,
  TrendingDown,
  ArrowDownCircle,
  ArrowUpCircle,
  Activity,
  DollarSign,
  Clock,
  RefreshCw,
  Sparkles,
  Calendar,
  ShieldCheck,
  ShieldAlert,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Loader2,
  Send,
} from 'lucide-react';

const Dashboard: React.FC = () => {
  const { t, language } = useLocalization();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState<DashboardStats | null>(null);
  const [balanceData, setBalanceData] = useState<BalanceData | null>(null);
  const [hourlyData, setHourlyData] = useState<HourlyChartData[]>([]);
  const [dailyStats, setDailyStats] = useState<DailyStats[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [operatorStatsData, setOperatorStatsData] = useState<OperatorStatsType | null>(null);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [refreshCountdown, setRefreshCountdown] = useState(120);
  const [isRefreshing, setIsRefreshing] = useState(false);

  useEffect(() => {
    const timeInterval = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timeInterval);
  }, []);

  useEffect(() => {
    const countdownInterval = setInterval(() => {
      setRefreshCountdown(prev => {
        if (prev <= 1) {
          handleAutoRefresh();
          return 120;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(countdownInterval);
  }, []);

  const fetchDashboardData = async () => {
    try {
      setIsRefreshing(true);
      setError(null);

      const [stats, balances, hourly, daily, operatorStats] = await Promise.all([
        dashboardService.getStats(),
        dashboardService.getBalances(),
        dashboardService.getHourlyChart(),
        dashboardService.getDailyStats(7),
        dashboardService.getOperatorStats()
      ]);

      setOperatorStatsData(operatorStats);
      setDashboardData(stats);
      setBalanceData(balances);
      setHourlyData(hourly);
      setDailyStats(daily.daily_stats || []);
    } catch (err: any) {
      if (err.response?.status === 401) return;
      setError(err.response?.data?.detail || 'Error loading data');
    } finally {
      setLoading(false);
      setIsRefreshing(false);
    }
  };

  const handleAutoRefresh = () => {
    if (!isRefreshing) fetchDashboardData();
  };

  useEffect(() => { fetchDashboardData(); }, []);

  const formatTimeWithSeconds = (date: Date) => {
    return date.toLocaleTimeString(language === 'fr' ? 'fr-FR' : 'en-US', {
      hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false
    });
  };

  // --- Computed metrics from by_operator ---
  const byOp = dashboardData?.by_operator || [];

  const totalSuccessful = byOp.reduce((s, o) => s + (o.successful_transactions || 0), 0);
  const totalFailed = byOp.reduce((s, o) => s + (o.failed_transactions || 0), 0);
  const totalPending = byOp.reduce((s, o) => s + (o.pending_transactions || 0), 0);
  const totalSubmitted = byOp.reduce((s, o) => s + (o.submitted_transactions || 0), 0);
  const totalTx = byOp.reduce((s, o) => s + (o.total_transactions || 0), 0);
  const successRate = totalTx > 0 ? Math.round((totalSuccessful / totalTx) * 100) : 0;

  // Revenue by currency (Successful only = total_amount from backend)
  const depositsCDF = byOp
    .filter(o => o.action === 'debit' && o.currency === 'CDF')
    .reduce((s, o) => s + (o.total_amount || 0), 0);
  const depositsUSD = byOp
    .filter(o => o.action === 'debit' && o.currency === 'USD')
    .reduce((s, o) => s + (o.total_amount || 0), 0);
  const payoutsCDF = byOp
    .filter(o => o.action === 'credit' && o.currency === 'CDF')
    .reduce((s, o) => s + Math.abs(o.total_amount || 0), 0);
  const payoutsUSD = byOp
    .filter(o => o.action === 'credit' && o.currency === 'USD')
    .reduce((s, o) => s + Math.abs(o.total_amount || 0), 0);

  // Yesterday comparison from daily stats
  const yesterday = dailyStats.length >= 2 ? dailyStats[dailyStats.length - 2] : null;
  const yesterdayRate = yesterday?.success_rate || 0;
  const rateDelta = successRate - yesterdayRate;

  // Success rate color
  const rateColor = successRate >= 90 ? 'emerald' : successRate >= 70 ? 'amber' : 'red';
  const rateColorMap: Record<string, { bg: string; text: string; icon: string; border: string }> = {
    emerald: { bg: 'bg-emerald-50', text: 'text-emerald-700', icon: 'text-emerald-500', border: 'border-emerald-200' },
    amber: { bg: 'bg-amber-50', text: 'text-amber-700', icon: 'text-amber-500', border: 'border-amber-200' },
    red: { bg: 'bg-red-50', text: 'text-red-700', icon: 'text-red-500', border: 'border-red-200' },
  };
  const rc = rateColorMap[rateColor];

  // Status distribution for donut
  const statusData = [
    { name: 'Successful', value: totalSuccessful, color: '#10b981' },
    { name: 'Failed', value: totalFailed, color: '#ef4444' },
    { name: 'Pending', value: totalPending, color: '#f59e0b' },
    { name: 'Submitted', value: totalSubmitted, color: '#3b82f6' },
  ].filter(d => d.value > 0);

  // Hourly chart data
  const hourlyChartData = hourlyData.map(h => ({
    hour: `${h.hour.toString().padStart(2, '0')}:00`,
    total: h.total_count,
    successful: h.successful_count,
    failed: h.failed_count,
    successRate: h.total_count > 0 ? Math.round((h.successful_count / h.total_count) * 100) : 0,
  }));

  // 7-day trend chart
  const dailyChartData = dailyStats.map(d => ({
    date: new Date(d.date).toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-US', { weekday: 'short', day: 'numeric' }),
    total: d.total_transactions,
    successful: d.successful_transactions,
    failed: d.failed_transactions,
    successRate: Math.round(d.success_rate),
  }));

  // Time-based greeting
  const hour = currentTime.getHours();
  const greetings = language === 'fr'
    ? { morning: 'Bonjour', afternoon: 'Bon apres-midi', evening: 'Bonsoir' }
    : { morning: 'Good morning', afternoon: 'Good afternoon', evening: 'Good evening' };
  const greeting = hour < 12 ? greetings.morning : hour < 18 ? greetings.afternoon : greetings.evening;
  const userName = user?.firstname || user?.username || "Merchant";
  const dateStr = currentTime.toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-US', {
    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
  });

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
        <div className="bg-white rounded-2xl shadow-lg border border-red-200 p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <XCircle className="w-8 h-8 text-red-600" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">{t("dashboard.loadingError") || "Loading Error"}</h3>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="w-full bg-gradient-to-r from-emerald-600 to-teal-600 text-white py-3 px-4 rounded-xl hover:from-emerald-700 hover:to-teal-700 transition-all duration-200 font-medium"
          >
            {t("dashboard.tryAgain") || "Try Again"}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      {/* Welcome Banner */}
      <div className="relative overflow-hidden rounded-2xl p-5 sm:p-6 text-white" style={{ background: 'linear-gradient(135deg, #667eea 0%, #5a67d8 100%)' }}>

        <div className="relative flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
          <div className="flex-1">
            <div className="inline-flex items-center gap-2 bg-white/10 border border-white/20 backdrop-blur-sm rounded-full px-3 py-1 mb-4">
              <Sparkles className="w-3 h-3 text-white/80" />
              <span className="text-[11px] font-semibold text-white/80 tracking-wider uppercase">
                {t("dashboard.realtimeAnalytics") || "Real-time Performance Analytics"}
              </span>
            </div>
            <h1 className="text-lg sm:text-xl lg:text-2xl font-bold tracking-tight mb-1.5">
              {greeting}, <span className="text-white font-extrabold">{userName}</span>
            </h1>
            <p className="text-xs sm:text-sm text-slate-400 flex items-center gap-2">
              <Calendar className="w-3.5 h-3.5" />
              <span className="capitalize">{dateStr}</span>
            </p>
          </div>

          <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
            <div className="flex items-center gap-2 bg-white/[0.06] backdrop-blur-sm border border-white/10 rounded-xl px-3.5 py-2.5">
              <Clock className="h-4 w-4 text-slate-400" />
              <span className="text-sm font-mono font-semibold text-white tabular-nums">
                {formatTimeWithSeconds(currentTime)}
              </span>
            </div>
            <div className="flex items-center gap-2 bg-white/10 border border-white/25 backdrop-blur-sm rounded-xl px-3.5 py-2.5">
              <div className="relative">
                <div className="absolute -inset-0.5 bg-white/40 rounded-full animate-ping" />
                <div className="relative w-2 h-2 bg-white rounded-full" />
              </div>
              <span className="text-sm font-semibold text-white/90">{t("dashboard.live") || "Live"}</span>
            </div>
            <button
              onClick={() => { setRefreshCountdown(120); fetchDashboardData(); }}
              className="flex items-center gap-2 bg-white/[0.06] backdrop-blur-sm border border-white/10 hover:bg-white/[0.1] hover:border-white/20 rounded-xl px-3.5 py-2.5 transition-all"
              disabled={isRefreshing}
            >
              <RefreshCw className={`h-4 w-4 text-slate-300 ${isRefreshing ? 'animate-spin' : ''}`} />
              <span className="text-xs text-slate-400 tabular-nums hidden sm:inline font-medium">{refreshCountdown}s</span>
            </button>
          </div>
        </div>
      </div>

      {/* Operators Performance (first, untouched) */}
      <OperatorStats
        dashboardData={dashboardData}
        balanceData={balanceData}
        operatorStatsData={operatorStatsData}
        loading={loading || isRefreshing}
      />

      {/* Transaction Health Overview */}
      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="bg-white rounded-2xl border border-gray-200/80 p-5 animate-pulse">
              <div className="h-4 bg-gray-200 rounded w-24 mb-3"></div>
              <div className="h-7 bg-gray-200 rounded w-32 mb-2"></div>
              <div className="h-3 bg-gray-100 rounded w-20"></div>
            </div>
          ))}
        </div>
      ) : (
        <>
          <div className="flex items-center gap-2 mt-2">
            <Activity className="w-4 h-4 text-gray-400" />
            <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wider">
              {language === 'fr' ? "Sante des transactions" : "Transaction Health"}
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Success Rate */}
            <div className={`relative bg-white rounded-2xl border ${rc.border} p-5 overflow-hidden`}>
              <div className={`absolute top-0 right-0 w-32 h-32 ${rc.bg} rounded-full blur-3xl opacity-50`} />
              <div className="relative">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    {language === 'fr' ? "Taux de succes" : "Success Rate"}
                  </span>
                  <div className={`h-10 w-10 rounded-xl ${rc.bg} flex items-center justify-center`}>
                    {successRate >= 90 ? <ShieldCheck className={`h-5 w-5 ${rc.icon}`} /> :
                     successRate >= 70 ? <ShieldAlert className={`h-5 w-5 ${rc.icon}`} /> :
                     <AlertTriangle className={`h-5 w-5 ${rc.icon}`} />}
                  </div>
                </div>
                <div className={`text-3xl font-bold ${rc.text} tabular-nums`}>{successRate}%</div>
                <div className="flex items-center gap-1 mt-2">
                  {rateDelta >= 0 ? (
                    <TrendingUp className="h-3.5 w-3.5 text-emerald-500" />
                  ) : (
                    <TrendingDown className="h-3.5 w-3.5 text-red-500" />
                  )}
                  <span className={`text-xs font-semibold ${rateDelta >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                    {rateDelta >= 0 ? '+' : ''}{rateDelta}% vs {language === 'fr' ? 'hier' : 'yesterday'}
                  </span>
                </div>
              </div>
            </div>

            {/* Deposits Received */}
            <div className="group relative bg-white rounded-2xl border border-gray-200/80 p-5 hover:shadow-lg hover:shadow-emerald-500/[0.05] hover:border-emerald-200 transition-all duration-300 overflow-hidden">
              <div className="absolute top-0 right-0 w-32 h-32 bg-emerald-500/[0.04] rounded-full blur-3xl group-hover:bg-emerald-500/[0.08] transition-all" />
              <div className="relative">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    {language === 'fr' ? "Depots recus" : "Deposits Received"}
                  </span>
                  <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center shadow-lg shadow-emerald-500/30">
                    <ArrowDownCircle className="h-5 w-5 text-white" />
                  </div>
                </div>
                <div className="space-y-1">
                  <div className="text-lg font-bold text-gray-900 tabular-nums">
                    {depositsCDF.toLocaleString()} <span className="text-sm font-medium text-gray-400">CDF</span>
                  </div>
                  <div className="text-sm font-semibold text-gray-600 tabular-nums">
                    {depositsUSD.toLocaleString()} <span className="text-xs font-medium text-gray-400">USD</span>
                  </div>
                </div>
                <div className="flex items-center gap-1 mt-2">
                  <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" />
                  <span className="text-xs text-emerald-600 font-semibold">Successful only</span>
                </div>
              </div>
            </div>

            {/* Payouts Sent */}
            <div className="group relative bg-white rounded-2xl border border-gray-200/80 p-5 hover:shadow-lg hover:shadow-amber-500/[0.05] hover:border-amber-200 transition-all duration-300 overflow-hidden">
              <div className="absolute top-0 right-0 w-32 h-32 bg-amber-500/[0.04] rounded-full blur-3xl group-hover:bg-amber-500/[0.08] transition-all" />
              <div className="relative">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    {language === 'fr' ? "Payouts envoyes" : "Payouts Sent"}
                  </span>
                  <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center shadow-lg shadow-amber-500/30">
                    <ArrowUpCircle className="h-5 w-5 text-white" />
                  </div>
                </div>
                <div className="space-y-1">
                  <div className="text-lg font-bold text-gray-900 tabular-nums">
                    {payoutsCDF.toLocaleString()} <span className="text-sm font-medium text-gray-400">CDF</span>
                  </div>
                  <div className="text-sm font-semibold text-gray-600 tabular-nums">
                    {payoutsUSD.toLocaleString()} <span className="text-xs font-medium text-gray-400">USD</span>
                  </div>
                </div>
                <div className="flex items-center gap-1 mt-2">
                  <CheckCircle2 className="h-3.5 w-3.5 text-amber-500" />
                  <span className="text-xs text-amber-600 font-semibold">Successful only</span>
                </div>
              </div>
            </div>

            {/* Issues / Alerts */}
            <div className={`group relative bg-white rounded-2xl border ${
              totalFailed > 0 ? 'border-red-200' : totalPending > 0 ? 'border-amber-200' : 'border-gray-200/80'
            } p-5 overflow-hidden`}>
              <div className={`absolute top-0 right-0 w-32 h-32 ${
                totalFailed > 0 ? 'bg-red-500/[0.06]' : 'bg-gray-500/[0.04]'
              } rounded-full blur-3xl`} />
              <div className="relative">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    {language === 'fr' ? "A surveiller" : "Requires Attention"}
                  </span>
                  <div className={`h-10 w-10 rounded-xl ${
                    totalFailed > 0 ? 'bg-red-50' : 'bg-gray-50'
                  } flex items-center justify-center`}>
                    <AlertTriangle className={`h-5 w-5 ${totalFailed > 0 ? 'text-red-500' : 'text-gray-400'}`} />
                  </div>
                </div>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-1.5">
                      <XCircle className="h-3.5 w-3.5 text-red-500" />
                      <span className="text-xs text-gray-600">{language === 'fr' ? 'Echouees' : 'Failed'}</span>
                    </div>
                    <span className={`text-sm font-bold tabular-nums ${totalFailed > 0 ? 'text-red-600' : 'text-gray-400'}`}>
                      {totalFailed}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-1.5">
                      <Loader2 className="h-3.5 w-3.5 text-amber-500" />
                      <span className="text-xs text-gray-600">Pending</span>
                    </div>
                    <span className={`text-sm font-bold tabular-nums ${totalPending > 0 ? 'text-amber-600' : 'text-gray-400'}`}>
                      {totalPending}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-1.5">
                      <Send className="h-3.5 w-3.5 text-blue-500" />
                      <span className="text-xs text-gray-600">Submitted</span>
                    </div>
                    <span className={`text-sm font-bold tabular-nums ${totalSubmitted > 0 ? 'text-blue-600' : 'text-gray-400'}`}>
                      {totalSubmitted}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Charts Row: Hourly Activity + Status Distribution */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Hourly Activity */}
            <div className="lg:col-span-2 bg-white rounded-2xl border border-gray-200/80 p-5 shadow-sm">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-sm font-semibold text-gray-900">
                    {language === 'fr' ? "Activite horaire" : "Hourly Activity"}
                  </h3>
                  <p className="text-xs text-gray-500 mt-0.5">
                    {language === 'fr' ? "Volume et taux de succes par heure" : "Volume and success rate by hour"}
                  </p>
                </div>
                <div className="flex items-center gap-3 text-xs">
                  <div className="flex items-center gap-1.5">
                    <div className="w-2.5 h-2.5 rounded-full bg-emerald-500"></div>
                    <span className="text-gray-500">Successful</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <div className="w-2.5 h-2.5 rounded-full bg-red-400"></div>
                    <span className="text-gray-500">Failed</span>
                  </div>
                </div>
              </div>

              {hourlyChartData.length > 0 ? (
                <ResponsiveContainer width="100%" height={280}>
                  <ComposedChart data={hourlyChartData} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
                    <defs>
                      <linearGradient id="colorSuccess" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#10b981" stopOpacity={0.3} />
                        <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                    <XAxis dataKey="hour" tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                    <YAxis yAxisId="left" tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                    <YAxis yAxisId="right" orientation="right" domain={[0, 100]} tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} tickFormatter={(v: number) => `${v}%`} />
                    <Tooltip
                      contentStyle={{ backgroundColor: '#1e293b', border: 'none', borderRadius: '8px', fontSize: '12px', color: '#f1f5f9' }}
                      formatter={(value: number, name: string) => {
                        if (name === 'successRate') return [`${value}%`, 'Success Rate'];
                        return [value, name === 'successful' ? 'Successful' : 'Failed'];
                      }}
                    />
                    <Bar yAxisId="left" dataKey="successful" fill="#10b981" radius={[4, 4, 0, 0]} name="successful" />
                    <Bar yAxisId="left" dataKey="failed" fill="#ef4444" radius={[4, 4, 0, 0]} name="failed" />
                    <Line yAxisId="right" type="monotone" dataKey="successRate" stroke="#6366f1" strokeWidth={2} dot={{ r: 3 }} name="successRate" />
                  </ComposedChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-[280px] flex items-center justify-center text-gray-400 text-sm">
                  {language === 'fr' ? "Pas encore de donnees aujourd'hui" : "No data yet today"}
                </div>
              )}
            </div>

            {/* Status Distribution */}
            <div className="bg-white rounded-2xl border border-gray-200/80 p-5 shadow-sm">
              <div className="mb-4">
                <h3 className="text-sm font-semibold text-gray-900">
                  {language === 'fr' ? "Distribution des statuts" : "Status Distribution"}
                </h3>
                <p className="text-xs text-gray-500 mt-0.5">
                  {totalTx} {language === 'fr' ? "transactions aujourd'hui" : "transactions today"}
                </p>
              </div>

              {statusData.length > 0 ? (
                <>
                  <ResponsiveContainer width="100%" height={200}>
                    <PieChart>
                      <Pie data={statusData} cx="50%" cy="50%" innerRadius={50} outerRadius={75} paddingAngle={3} dataKey="value" stroke="none">
                        {statusData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip contentStyle={{ backgroundColor: '#1e293b', border: 'none', borderRadius: '8px', fontSize: '12px', color: '#f1f5f9' }} />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="space-y-2 mt-2">
                    {statusData.map(s => (
                      <div key={s.name} className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: s.color }}></div>
                          <span className="text-xs text-gray-600">{s.name}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-bold text-gray-900 tabular-nums">{s.value}</span>
                          <span className="text-xs text-gray-400 tabular-nums">
                            ({totalTx > 0 ? Math.round((s.value / totalTx) * 100) : 0}%)
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <div className="h-[280px] flex items-center justify-center text-gray-400 text-sm">
                  {language === 'fr' ? "Aucune transaction" : "No transactions"}
                </div>
              )}
            </div>
          </div>

          {/* 7-Day Trend */}
          {dailyChartData.length > 1 && (
            <div className="bg-white rounded-2xl border border-gray-200/80 p-5 shadow-sm">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-sm font-semibold text-gray-900">
                    {language === 'fr' ? "Tendance 7 jours" : "7-Day Trend"}
                  </h3>
                  <p className="text-xs text-gray-500 mt-0.5">
                    {language === 'fr' ? "Volume quotidien et taux de succes" : "Daily volume and success rate"}
                  </p>
                </div>
                <div className="flex items-center gap-3 text-xs">
                  <div className="flex items-center gap-1.5">
                    <div className="w-2.5 h-2.5 rounded-full bg-emerald-500"></div>
                    <span className="text-gray-500">Successful</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <div className="w-2.5 h-2.5 rounded-full bg-red-400"></div>
                    <span className="text-gray-500">Failed</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <div className="w-6 h-0.5 bg-indigo-500"></div>
                    <span className="text-gray-500">Success %</span>
                  </div>
                </div>
              </div>

              <ResponsiveContainer width="100%" height={260}>
                <ComposedChart data={dailyChartData} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                  <YAxis yAxisId="left" tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                  <YAxis yAxisId="right" orientation="right" domain={[0, 100]} tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} tickFormatter={(v: number) => `${v}%`} />
                  <Tooltip
                    contentStyle={{ backgroundColor: '#1e293b', border: 'none', borderRadius: '8px', fontSize: '12px', color: '#f1f5f9' }}
                    formatter={(value: number, name: string) => {
                      if (name === 'successRate') return [`${value}%`, 'Success Rate'];
                      return [value, name === 'successful' ? 'Successful' : 'Failed'];
                    }}
                  />
                  <Bar yAxisId="left" dataKey="successful" fill="#10b981" radius={[4, 4, 0, 0]} name="successful" />
                  <Bar yAxisId="left" dataKey="failed" fill="#ef4444" radius={[4, 4, 0, 0]} name="failed" stackId="a" />
                  <Line yAxisId="right" type="monotone" dataKey="successRate" stroke="#6366f1" strokeWidth={2.5} dot={{ r: 4, fill: '#6366f1' }} name="successRate" />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          )}
        </>
      )}

      {/* Mobile refresh indicator */}
      {isRefreshing && (
        <div className="fixed bottom-6 left-1/2 transform -translate-x-1/2 bg-gray-900 text-white px-4 py-2.5 rounded-full shadow-xl flex items-center gap-2 z-50 animate-fade-in-up border border-white/10">
          <RefreshCw className="w-4 h-4 animate-spin" />
          <span className="text-sm font-medium">{t("dashboard.updating") || "Updating..."}</span>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
